/**
 * Notification Hooks
 *
 * React Query hooks for notification management.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { notificationKeys } from '@/lib/query-keys';

// Cache configuration
const POLL_INTERVAL = 30000; // 30 seconds

// ============================================================================
// Types
// ============================================================================

export interface Notification {
  id: string;
  type:
    | 'ISSUE_STATUS_CHANGED'
    | 'ISSUE_ASSIGNED'
    | 'ISSUE_COMMENTED'
    | 'ISSUE_UPDATED'
    | 'PROJECT_UPDATE_MENTION'
    | 'TEST_RUN_ASSIGNED'
    | 'RUNBOOK_STARTED'
    | 'RUNBOOK_COMPLETED'
    | 'RUNBOOK_ROLLED_BACK'
    | 'ACTION_DUE'
    | 'ACTION_OVERDUE'
    | 'ACTION_ASSIGNED'
    | 'ACTION_FAILED'
    | 'CRITICAL_ACTION_FAILED'
    | 'PROJECT_HEALTH_CHANGED';
  title: string;
  message: string;
  isRead: boolean;
  createdAt: string;
  issueId: string | null;
  metadata?: {
    i18n?: {
      [key: string]: string | undefined;
      issueTitle?: string;
      actorName?: string;
      projectName?: string;
      testPlanName?: string;
      testRunVersion?: string;
      oldStatus?: string;
      newStatus?: string;
      updatedFields?: string;
    };
  };
  issue: {
    id: string;
    title: string;
    project: {
      id: string;
      name: string;
    };
  } | null;
  actor: {
    id: string;
    name: string;
    profilePictureUrl: string | null;
  } | null;
}

interface UnreadCountResponse {
  count: number;
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Fetch unread notification count with polling
 */
export function useUnreadNotificationCount() {
  return useQuery({
    queryKey: notificationKeys.unreadCount(),
    queryFn: async () => {
      const response = await api.get<UnreadCountResponse>('/notifications/unread-count');
      return response.data ?? { count: 0 };
    },
    refetchInterval: POLL_INTERVAL,
    staleTime: 10000,
  });
}

/**
 * Fetch notifications list (fetches when enabled)
 */
export function useNotifications(enabled = true) {
  return useQuery({
    queryKey: notificationKeys.list(),
    queryFn: async () => {
      const response = await api.get<Notification[]>('/notifications?limit=20');
      return response.data ?? [];
    },
    enabled,
    staleTime: 5000,
  });
}

/**
 * Mark a single notification as read
 */
export function useMarkNotificationRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      await api.patch(`/notifications/${notificationId}/read`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

/**
 * Mark all notifications as read
 */
export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await api.post('/notifications/read-all');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

/**
 * Dismiss (delete) a single notification
 */
export function useDismissNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      await api.delete(`/notifications/${notificationId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

/**
 * Dismiss (delete) all notifications
 */
export function useDismissAllNotifications() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await api.delete('/notifications');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}
